import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export type CharacterDetails = {
  id: string;
  idea_id: string;
  author_id: string;
  name: string;
  role: string;
  description: string;
  avatar_url?: string;
  personality?: string;
  background?: string;
  goals?: string;
  relationships?: string;
  favorite_count: number;
  has_favorited: boolean;
  created_at: string;
};

export function useCharacter(characterId: string) {
  const [character, setCharacter] = useState<CharacterDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchCharacter();
  }, [characterId]);

  const fetchCharacter = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('characters')
        .select(`
          *,
          book_ideas!inner(author_id),
          favorites:character_favorites(count),
          has_favorited:character_favorites(user_id)
        `)
        .eq('id', characterId)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        setCharacter({
          ...data,
          author_id: data.book_ideas.author_id,
          favorite_count: data.favorites[0]?.count ?? 0,
          has_favorited: data.has_favorited.some(f => f.user_id === user?.id)
        });
      } else {
        setCharacter(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch character');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    if (!user || !character) return;

    try {
      const { data: existing } = await supabase
        .from('character_favorites')
        .select('id')
        .eq('character_id', character.id)
        .eq('user_id', user.id)
        .single();

      if (existing) {
        await supabase
          .from('character_favorites')
          .delete()
          .eq('character_id', character.id)
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('character_favorites')
          .insert({
            character_id: character.id,
            user_id: user.id
          });
      }

      await fetchCharacter();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to toggle favorite');
    }
  };

  return {
    character,
    loading,
    error,
    toggleFavorite
  };
}